// Full-text search over the docs. The pages are static JSX, so each one is
// rendered to static markup once (lazily, on first search), stripped to plain
// text, and cached — a 16-page index built in a few milliseconds beats
// filtering on titles alone, which made the search box near-useless.

import { createElement } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { ALL_DOCS, type DocPage } from "./registry";

let index: Map<string, string> | null = null;

function buildIndex(): Map<string, string> {
  const m = new Map<string, string>();
  for (const page of ALL_DOCS) {
    const html = renderToStaticMarkup(createElement(page.Component));
    const text = html
      .replace(/<[^>]+>/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&#x27;|&apos;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/\s+/g, " ")
      .trim();
    m.set(page.slug, text);
  }
  return m;
}

/** The page's full body text, original case. Built on first call, cached. */
export function docBodyText(page: DocPage): string {
  index ??= buildIndex();
  return index.get(page.slug) ?? "";
}

export interface Snippet {
  pre: string;
  hit: string;
  post: string;
}

/** An excerpt of the page's body around the first occurrence of any query
 *  term, split so the UI can highlight the hit. Null when no term appears in
 *  the body (a title/keyword-only match). */
export function snippetFor(page: DocPage, query: string): Snippet | null {
  const text = docBodyText(page);
  const lower = text.toLowerCase();
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  let at = -1;
  let term = "";
  for (const t of terms) {
    const i = lower.indexOf(t);
    if (i !== -1 && (at === -1 || i < at)) {
      at = i;
      term = t;
    }
  }
  if (at === -1) return null;

  // ~40 chars of lead-in and ~70 of follow-on, snapped to word boundaries.
  let start = Math.max(0, at - 40);
  if (start > 0) {
    const sp = text.indexOf(" ", start);
    if (sp !== -1 && sp < at) start = sp + 1;
  }
  let end = Math.min(text.length, at + term.length + 70);
  if (end < text.length) {
    const sp = text.lastIndexOf(" ", end);
    if (sp > at + term.length) end = sp;
  }
  return {
    pre: (start > 0 ? "…" : "") + text.slice(start, at),
    hit: text.slice(at, at + term.length),
    post: text.slice(at + term.length, end) + (end < text.length ? "…" : ""),
  };
}
